import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ProjectTask } from '@/types/odoo';
import { TaskDetailPanel } from './TaskDetailPanel';

/**
 * Modal shown when a task card is clicked in the dashboard scheduler. Reuses
 * TaskDetailPanel (description editor, saved on blur, + fase-documenten), so editing
 * here keeps everything intact.
 */
interface TaskDetailPopupProps {
  projectId: number;
  faseId: number | null;
  task: ProjectTask;
  installerNames: Record<string, string>;
  /** Overview boards ("Per dag"): show the details, write nothing. */
  readOnly?: boolean;
  onClose: () => void;
}

export function TaskDetailPopup({ projectId, faseId, task, installerNames, readOnly = false, onClose }: TaskDetailPopupProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[55] overflow-y-auto bg-black/40 sm:flex sm:items-start sm:justify-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Taak ${task.name}`}
    >
      {/* Full-screen sheet on phone; centered card on sm+. A click on the
          backdrop does not close it (Igor, 2026-10-26): only "Sluiten" or Escape. */}
      <div
        className="flex min-h-full w-full flex-col bg-white sm:my-8 sm:min-h-0 sm:max-w-2xl sm:bg-transparent"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-gray-200 bg-white px-4 py-2.5 sm:static sm:mb-2 sm:justify-end sm:border-0 sm:bg-transparent sm:p-0">
          <h2 className="truncate font-bold text-td-navy sm:hidden">Details en beschrijving</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex min-h-[40px] shrink-0 items-center rounded-lg bg-gray-100 px-4 text-sm font-semibold text-td-navy hover:bg-gray-200 sm:bg-white sm:px-3 sm:py-1.5 sm:shadow"
          >
            Sluiten
          </button>
        </div>
        <div className="p-3 sm:p-0">
          <TaskDetailPanel projectId={projectId} faseId={faseId} task={task} installerNames={installerNames} readOnly={readOnly} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
